import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  flexRender,
  getPaginationRowModel,
} from '@tanstack/react-table';
import { colors } from 'baseui/tokens';
import { PlusCircleSVG } from 'common/assets';
import stylesColors from 'common/styles/styles.colors';
import { generateId } from 'common/utils/string';
import isEmpty from 'lodash/isEmpty';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import TableCell from './cell';
import {
  HeaderContainer,
  ResizeBorder,
  StickyTHead,
  StickyTopTR,
  StyledTable,
  TBody,
  Td,
  Th,
  Tr,
} from './components/table';
import TableContainer from './components/table-container';
import TableContent from './components/table-content';
import TablePagination from './pagination';
import { ColumnDataType, TableEditableComponentProps } from './type';
import { useEditableTable } from './use-editable-table';
import getError from './utils/get-error';
import { Button } from '../buttons';
import Text from '../text';

export default function TableEditableComponent<T = Record<string, any>>(
  props: TableEditableComponentProps<T>,
) {
  const {
    data: _data,
    columns,
    dispatch,
    disabled,
    noAddRemoveRow,
    hasPagination,
    headerColor,
    defaultPageSize = props.hasPagination ? 15 : 999,
    name,
    errors,
    uniqueRowKey,
    onCustomDisabledCell,
    onCustomBackgroundColorCell,
    tableContainerStyle,
  } = props;
  const { t } = useTranslation();
  const tableId = React.useRef(
    `editable-table-${(Math.random() * 10000).toString()}`,
  );
  const { activeId, setActiveId } = useEditableTable();
  const [selectedRow, setSelectedRow] = React.useState<any>();
  const data: any = _data;
  const columnRef = React.useRef<any>();
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(defaultPageSize);
  const table = useReactTable({
    data,
    columns,
    state: {
      pagination: { pageIndex: page, pageSize },
    },
    getSubRows: (row) => row?.subRows || [],
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(hasPagination && { getPaginationRowModel: getPaginationRowModel() }),
  });
  const { getRowModel, getHeaderGroups } = table;
  const rows = getRowModel().rows;
  const headerGroups = getHeaderGroups();

  React.useEffect(() => {
    if (disabled) {
      setSelectedRow(undefined);
    }
  }, [disabled]);

  const onAddNewRow = React.useCallback(
    (event: KeyboardEvent) => {
      if (!disabled && event.ctrlKey && event.code === 'Enter') {
        dispatch({ type: 'addRow' });
        columnRef.current?.focus();
        return false;
      }
      if (event.code === 'Enter') {
        return false;
      }
    },
    [disabled, dispatch],
  );

  const onRemoveRow = React.useCallback(
    (event: KeyboardEvent) => {
      if (
        !disabled &&
        selectedRow !== undefined &&
        (event.metaKey || event.ctrlKey) &&
        (event.code === 'Backspace' || event.code === 'Delete') &&
        !!rows.length
      ) {
        const newLength = rows.length - 1;

        dispatch({ type: 'removeRow', rowIndex: selectedRow });
        setSelectedRow((prev) => {
          if (!newLength) {
            return undefined;
          }
          if (newLength === prev) {
            return newLength - 1;
          }
          return prev;
        });
        return false;
      }
      if (event.code === 'Enter') {
        return false;
      }
    },
    [disabled, dispatch, rows.length, selectedRow],
  );

  React.useEffect(() => {
    if (
      document &&
      !disabled &&
      !noAddRemoveRow &&
      activeId === tableId.current
    ) {
      document.addEventListener('keydown', onAddNewRow, false);
      document.addEventListener('keydown', onRemoveRow, false);

      return () => {
        document.removeEventListener('keydown', onAddNewRow, false);
        document.removeEventListener('keydown', onRemoveRow, false);
      };
    }
  }, [disabled, onAddNewRow, onRemoveRow, noAddRemoveRow, activeId]);

  const Pangination = React.useCallback(() => {
    const currentPageSize = table.getState().pagination.pageIndex * pageSize;

    const lastPageSize =
      currentPageSize + pageSize > data.length
        ? data.length
        : currentPageSize + pageSize || 0;

    return (
      <TablePagination
        {...{
          setPage,
          setPageSize,
          meta: {
            currentPageSize: currentPageSize + 1 || 0,
            lastPageSize,
            itemSize: data.length,
            currentPage: table.getState().pagination.pageIndex + 1,
            numPages: table.getPageCount(),
            pageSize,
          },
        }}
      />
    );
  }, [data.length, pageSize, table]);
  const rowData = (rows || []).map((row, i) => {
    // Generate a unique key for each row
    const _key = generateId();
    const key = uniqueRowKey ? row.original?.[uniqueRowKey] ?? _key : _key;

    // Process cells within each row
    const cells = (row || []).getVisibleCells().map((cell, cellIdx) => {
      const cellColumn: any = cell.column.columnDef;
      const cellId = `${key}.${cell.id}`;

      // Determine whether the cell is disabled
      const _disabled =
        cellColumn.disabled ||
        disabled ||
        onCustomDisabledCell?.({
          rowOriginal: row.original,
          accessorKey: cellColumn.accessorKey,
        });

      // Handle size constraints
      const { maxSize, minSize } = cellColumn;

      // Check for any errors
      const error =
        !isEmpty(errors) &&
        getError(
          errors,
          `${name ? `${name}.` : ''}${i}.${cellColumn.accessorKey}`,
        );

      // Custom background color logic
      const customBackgroundColorCell = onCustomBackgroundColorCell?.({
        rowOriginal: row.original,
        accessorKey: cellColumn.accessorKey,
      });

      // Determine background color based on various conditions
      const backgroundColor = customBackgroundColorCell
        ? customBackgroundColorCell
        : error
        ? `${stylesColors.redLightest}80`
        : _disabled
        ? colors.gray50
        : selectedRow === i
        ? `${stylesColors.primaryLightest}`
        : 'white';

      // Build props for the <Td> element (Table Data)
      const tdProps = {
        key: cellId,
        style: {
          width: cell.column.getSize(),
          minWidth: minSize,
          maxWidth: maxSize,
          position:
            cellColumn.stickyLeft || cellColumn.stickyRight
              ? 'sticky'
              : undefined,
          zIndex:
            cellColumn.stickyLeft || cellColumn.stickyRight ? 2 : undefined,
          left: cellColumn.stickyLeft ? 0 : undefined,
          right: cellColumn.stickyRight ? 0 : undefined,
          padding: 0,
          backgroundColor,
        } as React.CSSProperties,
      } as React.ComponentProps<typeof Td>;

      // Build props for the <TableCell> component
      const tableCellProps = {
        cell: cellColumn.cell,
        context: cell.getContext(),
        dispatch,
        columnIdx: cellIdx,
        disabled: _disabled,
        onFocus: (_, rowIdx, columnIdx) => {
          setActiveId(tableId.current);
          setSelectedRow((prev) => (prev !== rowIdx ? rowIdx : prev));
        },
        onBlur: (_, rowIdx, columnIdx) => {
          setSelectedRow((prev) => (prev !== rowIdx ? rowIdx : prev));
        },
        minNumber: cellColumn.minNumberKey
          ? cell.row.original[cellColumn.minNumberKey]
          : Number.isFinite(cellColumn.minNumber)
          ? cellColumn.minNumber
          : undefined,
        maxNumber: cellColumn.maxNumberKey
          ? cell.row.original[cellColumn.maxNumberKey]
          : Number.isFinite(cellColumn.maxNumber)
          ? cellColumn.maxNumber
          : undefined,
      } as React.ComponentProps<typeof TableCell>;

      return {
        tdProps,
        tableCellProps,
        cellId,
        rowId: key,
      };
    });

    return {
      cells,
      rowId: key,
    };
  });

  const headerData = (headerGroups || []).map((headerGroup) => {
    const headerId = headerGroup.id;
    const cells = headerGroup.headers.map((header, columnIdx) => {
      const column: any = header.column.columnDef;
      const thProps = {
        key: `${headerGroup.id}-${column.header}-${columnIdx}`,
        style: {
          width: header.getSize(),
          minWidth: column.minSize,
          ...(column.stickyLeft || column.stickyRight
            ? {
                position: 'sticky',
                zIndex: 2,
                backgroundColor: 'white',
              }
            : {}),
          left: column.stickyLeft ? 0 : undefined,
          right: column.stickyRight ? 0 : undefined,
          backgroundColor: headerColor || stylesColors.bg1,
        } as React.CSSProperties,
      };
      const cellProps = {
        isCheckbox:
          column.dataType === ColumnDataType.checkbox && column.checkboxNoLabel,
        content: flexRender(column.header, header.getContext()),
        variant: 'captionSemibold' as React.ComponentProps<
          typeof Text
        >['variant'],
      };
      const resizerBorderProps = {
        onMouseDown: header.getResizeHandler(),
        onTouchStart: header.getResizeHandler(),
        className: `resizer ${
          header.column.getIsResizing() ? 'isResizing' : ''
        }`,
        style: {
          transform: '',
        } as React.CSSProperties,
      } as React.ComponentProps<typeof ResizeBorder>;

      return {
        resizerBorderProps,
        cellProps,
        thProps,
      };
    });

    return {
      headerId,
      cells,
    };
  });

  const addRow = React.useMemo(() => {
    return {
      onClick: () => dispatch({ type: 'addRow' }),
      onKeyDown: (e) => {
        if (e.shiftKey && e.key === 'Enter') {
          e.stopPropagation();
        }
      },
      startEnhancer(color, size) {
        return <PlusCircleSVG color={color} size={size} />;
      },
    } as React.ComponentProps<typeof Button>;
  }, [dispatch]);

  const showAddButton = !disabled && !noAddRemoveRow;

  return (
    <TableContainer
      id={tableId.current}
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        setActiveId(tableId.current);
      }}
      onFocus={(e) => {
        e.stopPropagation();
        setActiveId(tableId.current);
      }}
      onBlur={() => {
        setActiveId(null);
      }}
      style={tableContainerStyle}
    >
      <TableContent>
        <StyledTable
          {...{
            style: {
              width: table.getTotalSize(),
            },
          }}
        >
          <StickyTHead>
            {(headerData || []).map((headerGroup) => {
              return (
                <StickyTopTR key={headerGroup.headerId}>
                  {headerGroup.cells.map((cell, columnIdx) => {
                    return (
                      <Th key={cell.thProps.key} style={cell.thProps.style}>
                        {cell.cellProps.isCheckbox ? (
                          cell.cellProps.content
                        ) : (
                          <HeaderContainer>
                            <Text variant={cell.cellProps.variant}>
                              {cell.cellProps.content}
                            </Text>
                          </HeaderContainer>
                        )}
                        <ResizeBorder
                          onMouseDown={cell.resizerBorderProps.onMouseDown}
                          onTouchStart={cell.resizerBorderProps.onTouchStart}
                          className={cell.resizerBorderProps.className}
                          style={cell.resizerBorderProps.style}
                        />
                      </Th>
                    );
                  })}
                </StickyTopTR>
              );
            })}
          </StickyTHead>
          <TBody>
            {(rowData || []).map((row) => {
              return (
                <Tr key={row.rowId}>
                  {row.cells.map((cell) => {
                    return (
                      <Td key={cell.cellId} style={cell.tdProps.style}>
                        <TableCell
                          cell={cell.tableCellProps.cell}
                          context={cell.tableCellProps.context}
                          dispatch={cell.tableCellProps.dispatch}
                          columnIdx={cell.tableCellProps.columnIdx}
                          disabled={cell.tableCellProps.disabled}
                          onFocus={cell.tableCellProps.onFocus}
                          onBlur={cell.tableCellProps.onBlur}
                          minNumber={cell.tableCellProps.minNumber}
                          maxNumber={cell.tableCellProps.maxNumber}
                        />
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </TBody>
        </StyledTable>
      </TableContent>
      {showAddButton && (
        <div
          style={{
            display: 'flex',
          }}
        >
          <Button
            buttonType="tertiary"
            startEnhancer={addRow.startEnhancer}
            onClick={addRow.onClick}
            onKeyDown={addRow.onKeyDown}
          >
            {t('common:add_row')}
          </Button>
        </div>
      )}
      {hasPagination && <Pangination />}
    </TableContainer>
  );
}
